import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { getIsOpPro } from "modules/account-root/selectors"
import {
  getSortedContributors,
  getSortedGroupContributors,
  getSelectedGroup,
  getSelectedGroupValue,
  getAvailableRollups,
  getCurrentGrouping,
  getContributorsSummaryText,
  getCsvData,
  hasReferenceEquity,
  getSelectedGroupSiblings,
} from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"

export default createModule({
  view,
  required: ["account", "modelId", "positionSetId", "positionSetType"],
  data: [{
    resource: "compositeConcentrationContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, expand, segment, classifications }) => {
      if (!selectedDate) { return null }

      return {
        id: `compositeConcentrationContributors-${modelId}${positionSetId}${positionSetType}${selectedDate}${expand}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date, interval: END_DATE) {
                composition(scaleFormat: PERCENT_EQUITY_GMV) {
                  date
                  gmv
                  referenceEquity
                  positionsCount
                  positions {
                    id
                    bloombergTicker
                    economicExposure
                    country
                    description
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    assetClass
                    assetSubclass
                    modelProviderId
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          positionSet: {
            id: positionSetId,
            type: convertPositionSetType(positionSetType),
            segment,
            expand,
          },
          date: selectedDate,
        },
      }
    },
  }, {
    resource: "compositeGroupedConcentrationContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, expand, currentGrouping, segment, classifications }) => {
      if (!currentGrouping) { return null }

      const queryGroupBy = getQueryGroupBy(currentGrouping)

      return {
        id: `compositeGroupedConcentrationContributors-${modelId}${positionSetId}${positionSetType}${selectedDate}${expand}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $groupBy: ContributorGroupType!, $groupById: String,  $tier: String) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date, interval: END_DATE) {
                composition(scaleFormat: PERCENT_EQUITY_GMV) {
                  compositionBy (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                    id
                    description
                    totalPercentEquity:economicExposure
                    contributors {
                      id
                      economicExposure
                      assetClass
                      assetSubclass
                      country
                      currency
                      description
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      bloombergTicker
                      modelProviderId
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          positionSet: {
            id: positionSetId,
            type: convertPositionSetType(positionSetType),
            segment,
            expand,
          },
          date: selectedDate,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    ...state.modules.concentrationContributors,
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    currentGrouping: getCurrentGrouping(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroup: getSelectedGroup(state, props),
    isOpPro: getIsOpPro(state),
    refEquityOverride: hasReferenceEquity(state),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    page: getPage(state),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
